import { useTranslate } from '@tolgee/react';
import { useCallback } from 'react';

import useGoogleDrive from '../../../../hooks/useGoogleDrive';
import { useMetrics } from '../../../../hooks/useMetrics';
import {
  DOWNLOAD_NOT_ALLOWED_KEY,
  DOWNLOAD_NOT_ALLOWED_MESSAGE,
  isDownloadNotAllowedError,
  useUploadGoogleDrive,
} from '../../../../hooks/useUploadGoogleDrive';
import { MaterialAttachment } from '../../../../hooks/useUploadMaterialAttachment';
import { PreferredMimeType } from '../../../../services/backendService/types';
import { getFileExtension } from '../../../../utils/fileUtils';
import { useGoogleDrivePicker } from '../../../_atoms/GoogleDrivePicker';
import { CallbackDoc } from '../../../_atoms/GoogleDrivePicker/typeDefs';

/**
 * Anything the teacher keeps in Google Drive, attached to the lesson.
 *
 * Under agent v2 there is no reason to sort Drive files into decks and
 * not-decks on the way in: `files` carries ids, the agent opens each one and
 * asks what it is for. So everything picked here becomes an attachment, deck
 * included — the same shape a local upload produces.
 *
 * The copy is the backend's: `copyFileToBucket` fetches from Drive, writes to
 * the tenant's bucket and creates the FileUpload whose id we attach. The
 * frontend never sees the bytes.
 */

const GOOGLE_SLIDES_MIME_TYPE = 'application/vnd.google-apps.presentation';
const GOOGLE_DOCS_MIME_TYPE = 'application/vnd.google-apps.document';

/**
 * The documents — everything the picker can offer that isn't a deck. A caller
 * with nowhere to put a slide deck (the generator's passage field) shows this
 * list on its own; a PDF stays in it, because a PDF is a document here
 * whatever was used to write it.
 */
export const DOCUMENT_DRIVE_MIME_TYPES = [
  GOOGLE_DOCS_MIME_TYPE,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
];

/** The decks, which only a caller with a deck slot has any use for. */
const DECK_DRIVE_MIME_TYPES = [
  GOOGLE_SLIDES_MIME_TYPE,
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
];

/**
 * What the picker will show. Drive holds far more than this, and a file we
 * cannot read is worse than one the teacher never saw offered — the failure
 * would land after the copy, with a lesson half-planned around it.
 */
export const SUPPORTED_DRIVE_MIME_TYPES = [
  ...DOCUMENT_DRIVE_MIME_TYPES,
  ...DECK_DRIVE_MIME_TYPES,
].join(',');

/**
 * Google-native files have no bytes to copy — Drive has to export them first,
 * and it needs to be told what to. Both of the Google types we take export to
 * PDF, Slides included. Everything else is copied as it is, which is what
 * omitting `preferredMimeType` means — so a real .pptx stored in Drive stays a
 * .pptx; only Google's own format is converted.
 */
export function exportFormatFor(driveMimeType: string): PreferredMimeType | undefined {
  if (
    driveMimeType === GOOGLE_SLIDES_MIME_TYPE ||
    driveMimeType === GOOGLE_DOCS_MIME_TYPE
  ) {
    return 'PDF';
  }
  return undefined;
}

/**
 * What the composer's attachment strip renders. A Google-native file arrives as
 * whatever it was exported to, so the tile has to describe the export rather
 * than the Drive original — a Google Doc shown as a PDF is the truth here.
 */
const EXPORTED_MIME_TYPES: Record<PreferredMimeType, string> = {
  PPTX: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  PDF: 'application/pdf',
};

const MAX_DRIVE_FILE_MB = 100;

export function useGoogleDriveMaterialUpload({
  tenantId,
  eventMetadata,
  onAttached,
  onError,
}: {
  tenantId: string;
  eventMetadata?: Record<string, unknown>;
  onAttached: (attachment: MaterialAttachment) => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslate();
  const metrics = useMetrics();
  const { isReady, isError } = useGoogleDrive(tenantId);
  const { upload: copyFromDrive, isUploading } = useUploadGoogleDrive(tenantId);

  const handlePickedFile = useCallback(
    (file: CallbackDoc) => {
      const fileSizeInMb = file.sizeBytes * 1e-6;
      metrics.logEvent('CreatePage.UseMyMaterials.GoogleDriveFileSelected', {
        ...eventMetadata,
        fileName: file.name,
        fileType: file.mimeType,
        // '' for a Google-native Doc/Slide — Drive names those with no
        // extension until it exports them.
        fileExtension: getFileExtension(file.name),
        fileSize: fileSizeInMb,
        tenantId,
      });

      // Google-native files report `sizeBytes: 0` — there is nothing stored to
      // measure until Drive exports them — so only check what we can.
      if (fileSizeInMb > MAX_DRIVE_FILE_MB) {
        onError(
          t(
            'Materials.GoogleDrive.FileTooLarge',
            'That file is too large. The limit is 100 MB.',
          ),
        );
        return;
      }

      const exportFormat = exportFormatFor(file.mimeType);

      copyFromDrive(
        { fileId: file.id, preferredMimeType: exportFormat },
        {
          onSuccess: ({ fileUploadId, filePath }) => {
            if (!fileUploadId) {
              // Nothing to attach without it: the agent is given ids, not paths.
              onError(
                t(
                  'Materials.GoogleDrive.CopyFailed',
                  "We couldn't add that file from Google Drive. Please try again.",
                ),
              );
              return;
            }
            onAttached({
              attachmentMethod: 'upload',
              fileUploadId,
              fileId: filePath,
              fileName: file.name,
              mimetype: exportFormat ? EXPORTED_MIME_TYPES[exportFormat] : file.mimeType,
            });
          },
          onError: error =>
            onError(
              isDownloadNotAllowedError(error)
                ? t(DOWNLOAD_NOT_ALLOWED_KEY, DOWNLOAD_NOT_ALLOWED_MESSAGE)
                : t(
                    'Materials.GoogleDrive.CopyFailed',
                    "We couldn't add that file from Google Drive. Please try again.",
                  ),
            ),
        },
      );
    },
    [copyFromDrive, eventMetadata, metrics, onAttached, onError, t, tenantId],
  );

  const { showFilePicker } = useGoogleDrivePicker(
    handlePickedFile,
    tenantId,
    'DOCS',
    SUPPORTED_DRIVE_MIME_TYPES,
  );

  return {
    openPicker: showFilePicker,
    isUploading,
    isReady,
    isError,
  };
}
